/**
 * Generiska utility-funktioner för feedback-hantering
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Markerar en kommentar som OK (fungerar för både kund och byggkonsult)
 * @param {string} estimatId - ID för estimatet
 * @param {string} commentKey - Nyckeln för kommentaren
 * @param {string} viewMode - 'consultant' eller 'customer'
 * @param {string} token - Auth token
 * @returns {Promise<Object>} - Uppdaterat estimat från servern
 */
export const markCommentAsOK = async (estimatId, commentKey, viewMode, token) => {
  // Byggkonsult använder byggkonsult-response (sparar svar)
  // Kund använder customer-reply (svarar på byggkonsultens kommentarer)
  const endpoint = viewMode === 'consultant' 
    ? `/api/estimat/${estimatId}/byggkonsult-response`
    : `/api/estimat/${estimatId}/customer-reply`;
  
  // För byggkonsult: skicka 'response' med texten "✓ Markerad som OK"
  // För kund: skicka bara 'commentKey' (backend hanterar resten)
  const body = viewMode === 'consultant'
    ? { commentKey, response: '✓ Markerad som OK' }
    : { commentKey };
  
  const response = await fetch(`${API_URL}${endpoint}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  });
  
  if (!response.ok) {
    // Försök hämta felmeddelande från backend
    let errorMessage = 'Kunde inte markera som OK';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
      console.error('❌ Backend error:', errorData);
    } catch (e) {
      console.error('❌ Could not parse error response');
    }
    throw new Error(errorMessage);
  }
  
  const data = await response.json();
  
  // Backend returnerar antingen { estimat: ... } eller { success: true, estimat: ... }
  // Returnera alltid estimatet för konsekvent hantering
  return data.estimat || data;
};

/**
 * Ångrar kundens "Markerad som OK" på en feedback-kommentar.
 * @param {string} estimatId - ID för estimatet
 * @param {string} commentKey - Nyckeln för kommentaren
 * @param {string} token - Auth token
 * @returns {Promise<Object>} - Uppdaterat estimat från servern
 */
export const undoMarkCommentAsOK = async (estimatId, commentKey, token) => {
  const response = await fetch(`${API_URL}/api/estimat/${estimatId}/customer-reply/undo-ok`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ commentKey })
  });

  if (!response.ok) {
    let errorMessage = 'Kunde inte ångra markeringen';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch (e) { /* ignore */ }
    throw new Error(errorMessage);
  }

  const data = await response.json();
  return data.estimat || data;
};

/**
 * Sparar en kommentar/svar (fungerar för både kund och byggkonsult)
 * @param {string} estimatId - ID för estimatet
 * @param {string} commentKey - Nyckeln för kommentaren
 * @param {string} text - Kommentarstexten
 * @param {string} viewMode - 'consultant' eller 'customer'
 * @param {string} token - Auth token
 * @returns {Promise<Object>} - Uppdaterat estimat från servern
 */
export const saveComment = async (estimatId, commentKey, text, viewMode, token) => {
  const endpoint = viewMode === 'consultant' 
    ? `/api/estimat/${estimatId}/byggkonsult-response`
    : `/api/estimat/${estimatId}/customer-response`;
  
  const bodyKey = viewMode === 'consultant' ? 'response' : 'comment';
  
  const response = await fetch(`${API_URL}${endpoint}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      commentKey,
      [bodyKey]: text
    })
  });
  
  if (!response.ok) {
    throw new Error('Kunde inte spara kommentar');
  }
  
  return await response.json();
};

